import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { FaBackward, FaForward, FaYoutube } from "react-icons/fa";

const formatSeconds = (totalSec) => {
  if (!Number.isFinite(totalSec) || totalSec < 0) return "0:00";
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = Math.floor(totalSec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

let _playerSeq = 0;

/**
 * KathakalaPlayer — shared YouTube IFrame player with seek controls and an
 * optional "now playing" pill. Used by EpisodeDetail and the public
 * Jeedigunta pages.
 *
 * Props:
 *   videoId         (string)            YouTube video id (required to render)
 *   startSec        (number, default 0) initial seek position
 *   endSec          (number, optional)  stop playback here (YouTube `end`)
 *   autoplay        (bool, default true) start playing as soon as it loads
 *   nowPlayingTitle (string, optional)  text shown in the now-playing pill
 *   nowPlayingMeta  (string, optional)  secondary line in the pill
 *   youtubeUrl      (string, optional)  link target for "Open in YouTube"
 *   onTimeUpdate    (fn)                called every 500ms with currentSec
 *   className       (string, optional)  wraps the whole container
 *
 * Imperative handle:
 *   .seekTo(sec)
 *   .playVideo()
 *   .pauseVideo()
 *   .getCurrentTime()
 *   .playSegment(startSec, endSec)  load and play just that stretch
 */
const KathakalaPlayer = forwardRef(
  (
    {
      videoId,
      startSec = 0,
      endSec,
      autoplay = true,
      nowPlayingTitle,
      nowPlayingMeta,
      youtubeUrl,
      onTimeUpdate,
      className = "",
    },
    ref
  ) => {
    const playerRef = useRef(null);
    const intervalRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    // Stable element id so multiple players can co-exist.
    const elementIdRef = useRef(`yt-player-${++_playerSeq}`);

    // Load the IFrame API once per page.
    useEffect(() => {
      if (!window.YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
    }, []);

    // (Re)create the player whenever videoId changes.
    useEffect(() => {
      if (!videoId) return;

      const create = () => {
        try {
          if (playerRef.current?.destroy) playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;

        playerRef.current = new window.YT.Player(elementIdRef.current, {
          videoId,
          playerVars: {
            autoplay: autoplay ? 1 : 0,
            start: Math.floor(startSec || 0),
            ...(endSec ? { end: Math.ceil(endSec) } : {}),
            rel: 0,
            modestbranding: 1,
          },
          events: {
            onReady: () => {
              if (intervalRef.current) clearInterval(intervalRef.current);
              intervalRef.current = setInterval(() => {
                if (playerRef.current?.getCurrentTime) {
                  const t = Math.floor(playerRef.current.getCurrentTime());
                  setCurrentTime(t);
                  if (onTimeUpdate) onTimeUpdate(t);
                }
              }, 500);
            },
          },
        });
      };

      if (window.YT && window.YT.Player) {
        create();
      } else {
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (prev) prev();
          create();
        };
      }

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId, startSec, endSec]);

    // Cleanup on unmount.
    useEffect(() => {
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        try {
          playerRef.current?.destroy?.();
        } catch (e) {}
      };
    }, []);

    const seekBy = useCallback((delta) => {
      if (playerRef.current?.getCurrentTime && playerRef.current?.seekTo) {
        const cur = playerRef.current.getCurrentTime();
        playerRef.current.seekTo(Math.max(0, cur + delta), true);
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        seekTo: (sec) => playerRef.current?.seekTo?.(sec, true),
        playVideo: () => playerRef.current?.playVideo?.(),
        pauseVideo: () => playerRef.current?.pauseVideo?.(),
        getCurrentTime: () =>
          playerRef.current?.getCurrentTime ? playerRef.current.getCurrentTime() : 0,
        playSegment: (s, e) =>
          playerRef.current?.loadVideoById?.({
            videoId,
            startSeconds: Math.floor(s || 0),
            ...(e ? { endSeconds: Math.ceil(e) } : {}),
          }),
      }),
      [videoId]
    );

    if (!videoId) return null;

    return (
      <section className={`bg-black ${className}`}>
        <div className="max-w-5xl mx-auto">
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <div id={elementIdRef.current} className="absolute inset-0 w-full h-full" />
          </div>

          <div className="bg-kk-ink/95 border-t border-kk-gold/10 px-3 md:px-4 py-3">
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-2 flex-wrap">
              {[
                { label: "-1m", sec: -60 },
                { label: "-30s", sec: -30 },
                { label: "-15s", sec: -15 },
                { label: "-10s", sec: -10 },
                { label: "-5s", sec: -5 },
              ].map((b) => (
                <button
                  key={b.label}
                  type="button"
                  onClick={() => seekBy(b.sec)}
                  className="px-2 py-1.5 md:px-3 md:py-1.5 bg-white/5 border border-kk-gold/15 rounded-lg text-kk-gold/70 text-[11px] md:text-xs font-mono hover:bg-kk-gold/10 hover:border-kk-gold/30 hover:text-kk-gold active:scale-95 transition-all"
                >
                  <FaBackward className="inline mr-1 text-[8px] opacity-50" />
                  {b.label}
                </button>
              ))}

              <div className="px-3 py-1.5 bg-kk-maroon/30 border border-kk-gold/20 rounded-lg text-kk-cream font-mono text-sm md:text-base min-w-[80px] text-center tabular-nums">
                {formatSeconds(currentTime)}
              </div>

              {[
                { label: "+5s", sec: 5 },
                { label: "+10s", sec: 10 },
                { label: "+15s", sec: 15 },
                { label: "+30s", sec: 30 },
                { label: "+1m", sec: 60 },
              ].map((b) => (
                <button
                  key={b.label}
                  type="button"
                  onClick={() => seekBy(b.sec)}
                  className="px-2 py-1.5 md:px-3 md:py-1.5 bg-white/5 border border-kk-gold/15 rounded-lg text-kk-gold/70 text-[11px] md:text-xs font-mono hover:bg-kk-gold/10 hover:border-kk-gold/30 hover:text-kk-gold active:scale-95 transition-all"
                >
                  {b.label}
                  <FaForward className="inline ml-1 text-[8px] opacity-50" />
                </button>
              ))}
            </div>

            {(nowPlayingTitle || youtubeUrl) && (
              <div className="flex items-center gap-3 bg-white/5 rounded-lg px-3 md:px-4 py-2 border border-kk-gold/10">
                {nowPlayingTitle && (
                  <div className="w-1 h-8 bg-kk-gold rounded-full flex-shrink-0 animate-pulse" />
                )}
                <div className="flex-1 min-w-0">
                  {nowPlayingTitle && (
                    <div className="text-kk-cream text-sm md:text-base font-semibold truncate">
                      {nowPlayingTitle}
                    </div>
                  )}
                  {nowPlayingMeta && (
                    <div className="text-kk-gold/70 text-xs md:text-sm truncate">
                      {nowPlayingMeta}
                    </div>
                  )}
                </div>
                {youtubeUrl && (
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-600/30 hover:text-red-300 text-xs md:text-sm whitespace-nowrap"
                  >
                    <FaYoutube /> YouTube
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }
);

KathakalaPlayer.displayName = "KathakalaPlayer";

export default KathakalaPlayer;
export { formatSeconds };
